// The one definition of the Case Sheet's section tabs.
//
// The tab strip, the More menu and CaseSheetWorkspace's panels all read this
// list, so they cannot drift apart. Design reference:
// docs/design/ip-case-sheet/IP_Case_Sheet_Wireframe.html → "Tab navigation".
//
// ── Groups ───────────────────────────────────────────────────────
//
//   critical   eligible for the strip; shown in `priority` order while they
//              fit beside the More button, then overflow into More ("Core")
//   secondary  always in More ("Other sections")
//
// Array order is DISPLAY order and never changes with width — a tab that fits
// stays where it was. `priority` only decides which critical tabs survive when
// space runs out (1 = last to leave). Promoting a tab (e.g. Fluids for an ICU-
// heavy hospital) is a one-line `group` change here.
//
// `module` is an optional grant that hides the tab entirely. No tab sets one
// today: viewing is the `ip` grant on the whole case sheet, and each section
// gates its own write actions. Don't add one without checking that section's
// read path — hiding a tab must not be the only thing protecting its data.

import type { PriorityTab } from '@/lib/priorityTabs';

export type TabId =
  | 'today' | 'face-sheet' | 'assessment' | 'progress' | 'nursing' | 'medications' | 'vitals'
  | 'fluids' | 'investigations' | 'procedures' | 'consults' | 'consents' | 'risk-screens'
  | 'orders' | 'care-plan' | 'discharge' | 'specialty' | 'timeline';

/** The strip/More fitting rule itself lives in lib/priorityTabs.ts, shared
 * with Patient Records. */
export type CaseSheetTab = PriorityTab<TabId>;

export const CASE_SHEET_TABS: readonly CaseSheetTab[] = [
  // Face sheet leads the strip (admission particulars, attenders, MLC and
  // payer are recorded first) but is consulted more than edited afterwards,
  // so it gives way before the round-time tabs.
  { id: 'face-sheet', label: 'Face sheet', group: 'critical', priority: 6 },
  { id: 'today', label: 'Today', group: 'critical', priority: 1 },
  { id: 'medications', label: 'Medications', group: 'critical', priority: 2 },
  { id: 'vitals', label: 'Vitals', group: 'critical', priority: 3 },
  { id: 'progress', label: 'Progress notes', group: 'critical', priority: 4 },
  { id: 'orders', label: 'Orders', group: 'critical', priority: 5 },
  { id: 'nursing', label: 'Nursing', group: 'critical', priority: 7 },
  { id: 'investigations', label: 'Investigations', group: 'critical', priority: 8 },
  { id: 'discharge', label: 'Discharge', group: 'critical', priority: 9 },
  { id: 'fluids', label: 'Fluids', group: 'secondary' },
  { id: 'assessment', label: 'Assessment', group: 'secondary' },
  { id: 'risk-screens', label: 'Risk screens', group: 'secondary' },
  { id: 'procedures', label: 'Procedures', group: 'secondary' },
  { id: 'consults', label: 'Consults', group: 'secondary' },
  { id: 'consents', label: 'Consents', group: 'secondary' },
  { id: 'care-plan', label: 'Care plan', group: 'secondary' },
  { id: 'specialty', label: 'Specialty', group: 'secondary' },
  { id: 'timeline', label: 'Timeline', group: 'secondary' },
];

/** Tabs this user may see: a tab with its own grant is absent (not
 * disabled) without it. Viewing the case sheet itself is gated upstream. */
export function visibleTabs(
  tabs: readonly CaseSheetTab[],
  can: (moduleId: string) => boolean,
): CaseSheetTab[] {
  return tabs.filter(t => !t.module || can(t.module));
}

/**
 * A request to open a tab AND bring a specific spot in it into view.
 *
 * `targets` are tried in order against `data-focus` tokens inside that tab's
 * panel (an element may carry several, space-separated, matched with `~=`):
 * the exact record first, then the control that fixes the gap, then the
 * section itself. The panel is the last resort, so a request always lands
 * somewhere visible rather than leaving the user at the top of the page
 * wondering whether the click did anything.
 */
export interface FocusRequest { tab: TabId; targets: string[] }

/** Where a `{type, id}` source reference opens. */
export const SOURCE_TAB: Record<string, TabId> = {
  vitals: 'vitals',
  medication_order: 'medications',
  medication_administration: 'medications',
  investigation: 'investigations',
  procedure: 'procedures',
  consult: 'consults',
  care_plan: 'care-plan',
  progress_note: 'progress',
  nursing_note: 'nursing',
  assessment: 'assessment',
  allergy: 'assessment',
  allergy_review: 'assessment',
  medication_review: 'assessment',
  encounter_diagnosis: 'progress',
  clinical_order: 'orders',
  fluid_balance: 'fluids',
  consent: 'consents',
  discharge_summary: 'discharge',
  transfer: 'timeline',
  document: 'timeline',
  admission: 'timeline',
};

/**
 * "Open record": the record itself when it has an id (`vitals:42`), else the
 * control that creates one (`vitals:new` — an alert saying no vitals were
 * recorded has nothing to point at, so it points at "+ Vitals"), else the
 * section for that record type.
 */
export function sourceFocus(ref: { type: string; id: number | null }): FocusRequest | null {
  const tab = SOURCE_TAB[ref.type];
  if (!tab) return null;
  const targets = ref.id != null ? [`${ref.type}:${ref.id}`] : [`${ref.type}:new`];
  return { tab, targets: [...targets, ref.type] };
}

/** Each MRD gap's fix, most specific first. */
const DEFICIENCY_FOCUS: Record<string, FocusRequest> = {
  no_attender: { tab: 'face-sheet', targets: ['attender:new', 'attenders'] },
  minor_without_lar: { tab: 'face-sheet', targets: ['attenders'] },
  consultant_not_identified: { tab: 'face-sheet', targets: ['consultant'] },
  mlc_unanswered: { tab: 'face-sheet', targets: ['mlc'] },
  assessment_unsigned: { tab: 'assessment', targets: ['assessment:sign', 'assessment:edit', 'assessment'] },
  assessment_late: { tab: 'assessment', targets: ['assessment'] },
  allergy_not_reviewed: { tab: 'assessment', targets: ['allergy_review:new', 'allergy_review'] },
  principal_diagnosis_missing: { tab: 'assessment', targets: ['diagnosis:new', 'diagnosis'] },
  verbal_order_not_countersigned: { tab: 'orders', targets: ['clinical_order:pending', 'clinical_order'] },
  unsigned_progress_notes: { tab: 'progress', targets: ['progress_note:unsigned', 'progress_note'] },
  dnar_without_consent: { tab: 'consents', targets: ['consent:new', 'consent'] },
  procedure_without_consent: { tab: 'consents', targets: ['consent:new', 'consent'] },
  no_vitals: { tab: 'vitals', targets: ['vitals:new', 'vitals'] },
  vitals_stale: { tab: 'vitals', targets: ['vitals:new', 'vitals'] },
  critical_result_unreviewed: { tab: 'investigations', targets: ['investigation:critical', 'investigation'] },
  // The discharge fields exist only while the editor is open, so the
  // fallback is the button that opens it.
  discharge_summary_unsigned: { tab: 'discharge', targets: ['discharge_summary:edit', 'discharge_summary'] },
  discharge_medications_not_reconciled: { tab: 'discharge', targets: ['discharge_medications', 'discharge_summary:edit', 'discharge_summary'] },
  no_follow_up: { tab: 'discharge', targets: ['follow_up', 'discharge_summary:edit', 'discharge_summary'] },
};

/** Where an MRD-checklist gap is fixed, down to the field when there is one. */
export function deficiencyFocus(key: string): FocusRequest | null {
  if (key.startsWith('no_risk_screen_')) return { tab: 'risk-screens', targets: ['risk_screen:new', 'risk_screen'] };
  return DEFICIENCY_FOCUS[key] ?? null;
}

/**
 * Where an MRD-checklist gap is fixed — the header chip's items open that
 * section. Keys are utils/caseSheetDeficiencies.js's; an unknown or
 * profile-level gap (a missing registration number lives on the user's
 * profile, not the case sheet) returns null and the item is not a link.
 */
export function deficiencyTab(key: string): TabId | null {
  return deficiencyFocus(key)?.tab ?? null;
}
